import { useMemo, useState } from "react";
import {
  AlertTriangle,
  ShieldAlert,
  Layers,
  Tags,
  ShoppingCart,
  FlaskConical,
} from "lucide-react";

/* -------------------------------------------------------------------------- */
/*  Utilities (copied from FeedCard so this file is fully self-contained)      */
/* -------------------------------------------------------------------------- */

const cn = (...classes) => classes.filter(Boolean).join(" ");

const formatLabel = (value) =>
  String(value ?? "")
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatFlag = (value) =>
  String(value ?? "")
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/[\s-]+/g, "_")
    .replace(/_+/g, "_")
    .toLowerCase()
    .trim();

const isDarkMode = () => {
  if (typeof document === "undefined") return true;
  return document.documentElement.classList.contains("dark");
};

/* -------------------------------------------------------------------------- */
/*  Palette (trimmed: no confidence / meter tokens, plus an amber alert tone)  */
/* -------------------------------------------------------------------------- */

export const getPalette = (t) => {
  const dark = isDarkMode();

  const accent = "#1c9dea";
  const accentSoft = "rgba(14,165,233,0.1)";
  const accentBorder = "rgba(14,165,233,0.24)";
  const accentText = dark ? "rgba(125,211,252,0.95)" : accent;

  return {
    dark,
    accent,

    cardBg:
      t?.cardBg ||
      (dark
        ? "linear-gradient(135deg, rgba(15,23,42,0.92), rgba(2,6,23,0.96))"
        : "linear-gradient(135deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98))"),
    cardBorder:
      t?.cardBorder || (dark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.1)"),
    cardShadow:
      t?.cardShadow ||
      (dark
        ? "0 16px 42px rgba(2,6,23,0.26)"
        : "0 16px 36px rgba(15,23,42,0.08)"),

    inputBg:
      t?.inputBg || (dark ? "rgba(15,23,42,0.44)" : "rgba(248,250,252,0.86)"),
    inputBorder:
      t?.inputBorder ||
      (dark ? "rgba(148,163,184,0.13)" : "rgba(15,23,42,0.1)"),

    activeBg: t?.activeBg || accentSoft,
    activeBorder: t?.activeBorder || accentBorder,
    activeText: t?.activeText || accentText,

    textPrimary:
      t?.textPrimary ||
      (dark ? "rgba(248,250,252,0.96)" : "rgba(15,23,42,0.94)"),
    textSecondary:
      t?.textSecondary ||
      (dark ? "rgba(226,232,240,0.78)" : "rgba(51,65,85,0.88)"),
    textMuted:
      t?.textMuted ||
      (dark ? "rgba(148,163,184,0.62)" : "rgba(100,116,139,0.72)"),

    divider:
      t?.divider || (dark ? "rgba(148,163,184,0.12)" : "rgba(15,23,42,0.1)"),

    screenshotFallback: dark ? "rgba(15,23,42,0.52)" : "rgba(241,245,249,0.9)",
    imageGradient: dark
      ? "linear-gradient(180deg, transparent, rgba(2,6,23,0.72))"
      : "linear-gradient(180deg, transparent, rgba(15,23,42,0.22))",

    flagBg: dark ? "rgba(14,165,233,0.075)" : "rgba(14,165,233,0.065)",
    flagBorder: dark ? "rgba(56,189,248,0.18)" : "rgba(14,165,233,0.16)",
    flagText: dark ? "rgba(186,230,253,0.9)" : "rgba(2,132,199,0.94)",

    classificationBg: dark ? "rgba(14,165,233,0.12)" : "rgba(14,165,233,0.08)",
    classificationBorder: dark
      ? "rgba(56,189,248,0.26)"
      : "rgba(14,165,233,0.2)",
    classificationText: dark
      ? "rgba(224,242,254,0.96)"
      : "rgba(2,132,199,0.96)",

    // Amber alert tone — takedowns are safety notices, not neutral feed items.
    alertBg: dark ? "rgba(245,158,11,0.12)" : "rgba(217,119,6,0.09)",
    alertBorder: dark ? "rgba(251,191,36,0.28)" : "rgba(217,119,6,0.2)",
    alertText: dark ? "rgba(253,230,138,0.96)" : "rgba(180,83,9,0.96)",
  };
};

/* -------------------------------------------------------------------------- */
/*  Data helpers                                                               */
/* -------------------------------------------------------------------------- */

export const getScreenshot = (article) =>
  article.screenshot_full ||
  article.screenshotFull ||
  article.screenshot ||
  article.image_url ||
  article.imageUrl ||
  article.thumbnail ||
  (Array.isArray(article.images) ? article.images[0] : null) ||
  null;

const asArray = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

// De-dupe purchase listings by URL, preferring a confirmed status over anything else.
const dedupeListings = (listings) => {
  const byUrl = new Map();

  for (const listing of asArray(listings)) {
    const url = listing?.url;
    if (!url) continue;

    const existing = byUrl.get(url);
    if (
      !existing ||
      (listing.status === "confirmed" && existing.status !== "confirmed")
    ) {
      byUrl.set(url, listing);
    }
  }

  return [...byUrl.values()];
};

// Flatten the takedown doc into the fields the card renders. Fields are top-level in
// real data; tolerate a `meta`-nested doc too (top-level wins on conflict), mirroring
// the flatten in Universal/takedown/TakedownDetailView.jsx.
const getTakedown = (article = {}) => {
  const src = { ...(article.meta || {}), ...article };
  const availability = src.us_availability || {};

  return {
    name: src.product || src.title || "",
    subject: src.subject || src.description || "",
    summary: src.summary || src.description || src.content || "",
    action: src.action || src.action_label || "",
    source: src.source || "",
    recommendation: src.fda_recommendation || "",
    programAreas: asArray(src.program_areas),
    hiddenIngredients: asArray(src.hidden_ingredients),
    healthRisks: asArray(src.health_risks),
    availableInUs: Boolean(availability.available_in_us),
    listings: dedupeListings(availability.listings),
  };
};

/* -------------------------------------------------------------------------- */
/*  Presentational sub-components                                              */
/* -------------------------------------------------------------------------- */

const MetaBadge = ({ icon: Icon, children, p, tone = "accent" }) => {
  const colors =
    tone === "alert"
      ? { text: p.alertText, bg: p.alertBg, border: p.alertBorder }
      : {
          text: p.classificationText,
          bg: p.classificationBg,
          border: p.classificationBorder,
        };

  return (
    <div
      className="inline-flex h-[24px] max-w-full items-center gap-1.5 rounded-md px-2.5 text-[11px] font-medium leading-none tracking-[0.01em]"
      style={{
        color: colors.text,
        background: colors.bg,
        border: `1px solid ${colors.border}`,
      }}
    >
      {Icon && <Icon size={12} style={{ flexShrink: 0, opacity: 0.82 }} />}
      <span className="truncate">{children}</span>
    </div>
  );
};

const FlagBadge = ({ children, p }) => (
  <span
    className="inline-flex h-[23px] max-w-full items-center gap-1.5 rounded-md px-2 text-[11px] leading-none tracking-[0.01em]"
    style={{
      color: p.flagText,
      background: p.flagBg,
      border: `1px solid ${p.flagBorder}`,
    }}
  >
    <Tags size={11} style={{ flexShrink: 0, opacity: 0.72 }} />
    <span className="truncate">{children}</span>
  </span>
);

const DetailBlock = ({
  icon: Icon,
  label,
  children,
  items,
  p,
  tone,
  maxItems,
  clamp,
}) => {
  const isAlert = tone === "alert";
  const labelColor = isAlert ? p.alertText : p.textMuted;
  const borderColor = isAlert ? p.alertBorder : p.inputBorder;
  const bg = isAlert ? p.alertBg : p.inputBg;

  const shown = items && maxItems ? items.slice(0, maxItems) : items;
  const extra = items ? items.length - (shown ? shown.length : 0) : 0;

  return (
    <div
      className="rounded-xl p-3"
      style={{ background: bg, border: `1px solid ${borderColor}` }}
    >
      <div
        className="mb-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em]"
        style={{ color: labelColor }}
      >
        {Icon && <Icon size={12} />}
        {label}
      </div>

      {items ? (
        <ul className="flex flex-col gap-1">
          {shown.map((item, index) => (
            <li
              key={index}
              className="flex gap-1.5 text-sm leading-relaxed"
              style={{ color: p.textSecondary }}
            >
              <span aria-hidden style={{ color: p.textMuted }}>
                •
              </span>
              <span className={clamp ? "line-clamp-1" : ""}>{item}</span>
            </li>
          ))}
          {extra > 0 && (
            <li className="text-xs" style={{ color: p.textMuted }}>
              +{extra} more
            </li>
          )}
        </ul>
      ) : (
        <p
          className={`text-sm leading-relaxed${clamp ? " line-clamp-2" : ""}`}
          style={{ color: p.textSecondary }}
        >
          {children}
        </p>
      )}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*  Main card — display only (no click-through, no bookmark)                   */
/* -------------------------------------------------------------------------- */

export const TakedownCard = ({ article, t, typeBadge }) => {
  const p = useMemo(() => getPalette(t), [t]);
  const data = useMemo(() => getTakedown(article || {}), [article]);

  const [screenshotBroken, setScreenshotBroken] = useState(false);
  const [expandedSummary, setExpandedSummary] = useState(false);

  const screenshot = getScreenshot(article || {});
  const showScreenshot = Boolean(screenshot) && !screenshotBroken;

  return (
    <article
      className="relative self-start overflow-hidden rounded-2xl"
      style={{
        background: p.cardBg,
        border: `1px solid ${p.cardBorder}`,
        boxShadow: p.cardShadow,
      }}
    >
      <div className="flex h-full flex-col">
        {showScreenshot && (
          <div
            className="relative aspect-[16/9] w-full overflow-hidden border-b"
            style={{ borderColor: p.divider }}
          >
            <img
              src={screenshot}
              alt=""
              className="h-full w-full object-cover object-top"
              onError={() => setScreenshotBroken(true)}
            />
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 h-28"
              style={{ background: p.imageGradient }}
            />
          </div>
        )}

        <div className="flex flex-1 flex-col gap-3 p-4">
          {typeBadge && (
            <div className="flex flex-wrap gap-1.5">{typeBadge}</div>
          )}

          {(data.name || data.subject) && (
            <div className="flex flex-col gap-1">
              {data.name && (
                <h3
                  className="text-[17px] font-semibold leading-snug"
                  style={{ color: p.textPrimary }}
                >
                  {data.name}
                </h3>
              )}
              {data.subject && (
                <p
                  className="text-[13px] leading-snug"
                  style={{ color: p.textMuted }}
                >
                  {data.subject}
                </p>
              )}
            </div>
          )}

          {data.summary && (
            <div>
              <p
                className={cn(
                  "text-[15px] leading-relaxed",
                  expandedSummary ? "" : "line-clamp-3",
                )}
                style={{ color: p.textSecondary }}
              >
                {data.summary}
              </p>

              {data.summary.length > 190 && (
                <button
                  type="button"
                  className="mt-1 text-xs transition-opacity hover:opacity-80"
                  style={{ color: p.activeText }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedSummary((v) => !v);
                  }}
                >
                  {expandedSummary ? "Show less" : "Read more"}
                </button>
              )}
            </div>
          )}

          {data.recommendation && (
            <DetailBlock
              icon={ShieldAlert}
              label="FDA recommendation"
              p={p}
              tone="alert"
              clamp
            >
              {data.recommendation}
            </DetailBlock>
          )}

          {data.healthRisks.length > 0 && (
            <DetailBlock
              icon={AlertTriangle}
              label="Health risks"
              p={p}
              tone="alert"
              items={data.healthRisks}
              maxItems={1}
              clamp
            />
          )}

          {data.hiddenIngredients.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {data.hiddenIngredients.map((ingredient) => (
                <FlagBadge key={ingredient} p={p}>
                  {formatFlag(ingredient)}
                </FlagBadge>
              ))}
            </div>
          )}

          {data.listings.length > 0 && (
            <div
              className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em]"
              style={{ color: p.alertText }}
            >
              <ShoppingCart size={12} />
              {data.availableInUs ? "On sale in the US" : "Purchase locations"} ·{" "}
              {data.listings.length}{" "}
              {data.listings.length === 1 ? "site" : "sites"}
            </div>
          )}

          {(data.action || data.programAreas.length > 0 || data.source) && (
            <div className="flex flex-wrap gap-1.5">
              {data.action && (
                <MetaBadge icon={FlaskConical} p={p}>
                  {formatLabel(data.action)}
                </MetaBadge>
              )}
              {data.programAreas.map((area) => (
                <MetaBadge key={area} icon={Layers} p={p}>
                  {formatLabel(area)}
                </MetaBadge>
              ))}
              {data.source && (
                <MetaBadge p={p}>{data.source.toUpperCase()}</MetaBadge>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default TakedownCard;
